import { P2GAlert, P2GLink } from '@parcel2go/core-ui';
import { P2GButton } from '@parcel2go/core-ui';
import { SvgEnquiryMoreInfo } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { useState } from 'react';
import { Country, InputRuleApiModel, PackageApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import AlertWithIcon from '../../AlertWithIcon';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import { GenerateSplitSummary } from './GroupingHelper';
import { ParcelArranger } from './ParcelArranger';
import { ShipmentDimensionsSection } from './ConsignmentBuilder';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { ParcelCountPicker } from '../panels/ParcelCountPicker';
import { MIN_PARCEL_OPTIONS } from '~/apps/smartsend/lib/parcels/bounds';
import { linesToContents, replicateIntoParcels } from '~/apps/smartsend/lib/parcels/allocate';
import { contentIsIncomplete } from '~/apps/smartsend/lib/parcels/rules';
import { useParcelPlan } from '~/apps/smartsend/lib/parcels/useParcelPlan';
import StickyButtons from '~/components/sticky';

export interface props {
  group: SmartSendItemGroup;
  split: (item: SmartSendItemApiModel, newItems: SmartSendItemApiModel[]) => void;
  countries?: Country[];
  productValidation?: InputRuleApiModel[];
  tariffSystem?: string;
  cancel?: () => void;
  getPackages?: () => Promise<PackageApiModel[]>;
}

export const Splitting: React.FC<props> = (props) => {
  const item = props.group.Items[0];
  const [initialDims] = useState(() => ({ ...item.PackageDimensions }));
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  // Replicate rather than distribute: one physical item is going into several boxes, so every box
  // declares the same contents with the value divided between them.
  const { plan, parcelCount, dimensions, setParcelCount, setDimensions, edit } = useParcelPlan({
    initialParcelCount: 2,
    initialDimensions: initialDims,
    seed: (count, dims) =>
      replicateIntoParcels(linesToContents(item.MarketplaceOrderLines), count, {
        granularity: 'units',
        overflow: 'clamp',
        parcelId: () => item.Id,
        dimensions: dims,
        // Boxes here are fixed - the user edits each box's declaration, they do not move contents
        // between boxes.
        canAdd: false,
        // Divided, to match the Value that replicateIntoParcels already divides. Undivided, each box
        // declared the WHOLE order weight, so splitting a 0.7kg order in two produced a 1.4kg shipment -
        // and GenerateSplitSummary wrote that full weight onto every created shipment, so it was quoted
        // and booked that way, not merely displayed.
        splitWeight: true
      })
  });

  // Every row across every box needs a description, a positive quantity and a positive value before
  // this can be confirmed - these become the customs declaration for each resulting shipment.
  const hasInvalidContent = contentIsIncomplete(plan);

  /**
   * Which dimensions control is on screen. Never both: applyDimensions pushes the entered size onto
   * every box and re-divides the weight, so a shipment-wide edit would overwrite per-box entries.
   */
  const [previewing, setPreviewing] = useState(true);

  function createSplitItems() {
    const summary = GenerateSplitSummary(props.group, parcelCount, plan.Parcels, dimensions);
    if (summary) {
      props.split(summary.item, summary.newItems);
    }
  }

  return (
    <SidePanelSections>
      <P2GText>
        {t('CUSTOM_SPLIT_INTRO_TEXT', {
          defaultValue:
            'This will allow you to split your items so they can be collected at different times or on different services.'
        })}
      </P2GText>
      <SidePanelSection
        title={t('SHIPMENT_BOXES_BEING_SHIPPED', { defaultValue: 'How many boxes are you going to use?' })}
      >
        {/* This flow replicates the declaration into every box rather than allocating units, so
              there is no unit bound - one item can always go into another box. */}
        <ParcelCountPicker availableParcels={MIN_PARCEL_OPTIONS} selected={parcelCount} onSelect={setParcelCount} />
      </SidePanelSection>
      {parcelCount > 1 ? (
        <SidePanelSection title={t('SHIPMENT_SUMMARY_SUBTITLE', { defaultValue: 'Shipment Summary' })}>
          {/* No `arrange`: the boxes are fixed and the user edits each box's declaration. */}
          <ParcelArranger
            plan={plan}
            outcome="ONE_SHIPMENT"
            edit={edit}
            preview={previewing}
            onPreviewChange={setPreviewing}
            perParcelDimensions={{ getPackages: props.getPackages }}
            content={{
              countries: props.countries,
              productValidation: props.productValidation,
              tariffSystem: props.tariffSystem,
              sender: item?.Sender,
              recipient: item?.Recipient,
              currencyCode: item?.CurrencyCode
            }}
            loadPreview={true}
          />
        </SidePanelSection>
      ) : (
        <AlertWithIcon color="warning" icon={<SvgEnquiryMoreInfo />}>
          <P2GText variant="body2">
            {t('SPLIT_COUNT_TOO_LOW_WARNING', {
              defaultValue:
                'Please select a number of parcels greater than 1 to split this shipment into multiple parcels.'
            })}
            <P2GLink onClick={() => props.cancel?.()}>{t('CANCEL_LINK', { defaultValue: 'Cancel' })}</P2GLink>
          </P2GText>
        </AlertWithIcon>
      )}
      {parcelCount > 1 && previewing && (
        <ShipmentDimensionsSection
          dimensions={dimensions}
          parcelCount={parcelCount}
          fallback={initialDims}
          onUpdate={setDimensions}
          getPackages={props.getPackages}
        />
      )}
      {parcelCount > 1 && (
        <P2GAlert type="success">
          <P2GText variant="body2">
            {t('SPLIT_SUMMARY_TEXT', {
              defaultValue: 'This will split this order into {{parcelCount}} separate shipments.',
              parcelCount
            })}
          </P2GText>
        </P2GAlert>
      )}
      <StickyButtons>
        <P2GButton variant="primary" disabled={parcelCount <= 1 || hasInvalidContent} onClick={createSplitItems}>
          {t('CONFIRM_CREATE_BUTTON', { defaultValue: 'Confirm and Create' })}
        </P2GButton>
      </StickyButtons>
    </SidePanelSections>
  );
};
